/**
 * Hidden "debug mode" that unlocks experimental / in-progress admin tooling.
 *
 * Currently gates:
 *   - the Delete button on result templates (Results → Templates)
 *   - the school-wide Assessment Types manager tab (Results → Assessment Types)
 *
 * Both the UI affordances AND the GraphQL calls behind them stay dormant until
 * debug mode is switched on. It is intentionally undiscoverable in normal use:
 * toggle it with the key chord Ctrl + Shift + Alt + D. The state is persisted to
 * localStorage so it survives reloads, and is shared across the whole app via a
 * module-level singleton ref.
 */

const STORAGE_KEY = 'sms_debug_mode';

/** Key chord that toggles debug mode: Ctrl + Shift + Alt + D. */
export function isDebugToggleChord(e: KeyboardEvent): boolean {
  return e.ctrlKey && e.shiftKey && e.altKey && (e.code === 'KeyD' || e.key.toLowerCase() === 'd');
}

function readInitial(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

// Module-level singleton — every importer shares this exact reactive state.
const enabled = ref<boolean>(readInitial());
let listenerBound = false;

function persist(value: boolean) {
  try {
    if (value) localStorage.setItem(STORAGE_KEY, 'true');
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* storage unavailable (private mode / quota) — keep in-memory only */
  }
}

function setEnabled(value: boolean) {
  enabled.value = value;
  persist(value);
}

/**
 * Bind the global hotkey listener. Idempotent — safe to call more than once,
 * only the first call actually attaches a listener. Call from the app root.
 * Returns a cleanup function that detaches the listener.
 */
export function bindDebugModeHotkey(onToggle?: (active: boolean) => void): () => void {
  if (listenerBound || typeof window === 'undefined') return () => {};

  const handler = (e: KeyboardEvent) => {
    if (!isDebugToggleChord(e)) return;
    e.preventDefault();
    setEnabled(!enabled.value);
    onToggle?.(enabled.value);
  };

  window.addEventListener('keydown', handler);
  listenerBound = true;

  return () => {
    window.removeEventListener('keydown', handler);
    listenerBound = false;
  };
}

export function useDebugMode() {
  return {
    /** Reactive flag. Destructure (`const { enabled } = useDebugMode()`) so it auto-unwraps in templates. */
    enabled: readonly(enabled),
    enable: () => setEnabled(true),
    disable: () => setEnabled(false),
    toggle: () => setEnabled(!enabled.value),
  };
}
